import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { translations } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AppContext = createContext(null);

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};

export const AppProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "fr");
  const [user, setUser] = useState(null); // null = unknown, false = anon, obj = user

  useEffect(() => { localStorage.setItem("lang", lang); }, [lang]);

  const t = translations[lang] || translations.fr;

  // Cookie-based auth (httpOnly); withCredentials sends the access_token cookie.
  const authHeaders = useCallback(() => ({ withCredentials: true }), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (!cancelled) setUser(res.data);
      } catch (err) {
        if (!cancelled) setUser(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser({ id: res.data.id, email: res.data.email, name: res.data.name });
    return res.data;
  };

  const register = async (email, password, name) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
    setUser({ id: res.data.id, email: res.data.email, name: res.data.name });
    return res.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error("logout failed", err);
    }
    setUser(false);
  };

  return (
    <AppContext.Provider value={{ lang, setLang, t, user, login, register, logout, authHeaders, API }}>
      {children}
    </AppContext.Provider>
  );
};
